/*
 * Tiny isometric projection helpers for the hand-drawn switch illustration.
 * x runs to the lower right, y to the lower left and z straight up.
 * Values are rounded so the server and the browser render identical markup.
 */

export type Point3 = [number, number, number]

export interface BoxFaces {
    top: string
    right: string
    left: string
}

export interface CylinderShape {
    cx: number
    topY: number
    bottomY: number
    rx: number
    ry: number
    side: string
}

export interface PrismFaces {
    sides: string[]
    top: string
}

const COS30 = Math.cos(Math.PI / 6)

export const round = (value: number): number => Number(value.toFixed(2))

export const project = ([x, y, z]: Point3): [number, number] => [round((x - y) * COS30), round((x + y) * 0.5 - z)]

export const toPoints = (list: Point3[]): string => list.map(point => project(point).join(',')).join(' ')

export const box = (cx: number, cy: number, z0: number, a: number, b: number, h: number): BoxFaces => {
    const z1 = z0 + h

    return {
        top: toPoints([[cx - a, cy - b, z1], [cx + a, cy - b, z1], [cx + a, cy + b, z1], [cx - a, cy + b, z1]]),
        right: toPoints([[cx + a, cy - b, z1], [cx + a, cy + b, z1], [cx + a, cy + b, z0], [cx + a, cy - b, z0]]),
        left: toPoints([[cx - a, cy + b, z1], [cx + a, cy + b, z1], [cx + a, cy + b, z0], [cx - a, cy + b, z0]])
    }
}

export const cylinder = (x: number, y: number, r: number, z0: number, z1: number): CylinderShape => {
    const [cx, bottomY] = project([x, y, z0])
    const [, topY] = project([x, y, z1])
    const rx = round(r * Math.SQRT2 * COS30)
    const ry = round(r * Math.SQRT2 * 0.5)
    const left = round(cx - rx)
    const right = round(cx + rx)

    return {
        cx,
        topY,
        bottomY,
        rx,
        ry,
        side: `M ${left} ${topY} L ${left} ${bottomY} A ${rx} ${ry} 0 0 0 ${right} ${bottomY} L ${right} ${topY} Z`
    }
}

/* A plus shaped prism, like the cross on top of a switch stem. */
export const cross = (cx: number, cy: number, z0: number, arm: number, half: number, h: number): PrismFaces => {
    const z1 = z0 + h
    const face = (a: [number, number], b: [number, number]) => toPoints([
        [cx + a[0], cy + a[1], z1],
        [cx + b[0], cy + b[1], z1],
        [cx + b[0], cy + b[1], z0],
        [cx + a[0], cy + a[1], z0]
    ])

    return {
        sides: [
            face([-arm, half], [-half, half]),
            face([half, -arm], [half, -half]),
            face([arm, -half], [arm, half]),
            face([half, half], [arm, half]),
            face([half, half], [half, arm]),
            face([-half, arm], [half, arm])
        ],
        top: toPoints([
            [cx - arm, cy - half, z1],
            [cx - half, cy - half, z1],
            [cx - half, cy - arm, z1],
            [cx + half, cy - arm, z1],
            [cx + half, cy - half, z1],
            [cx + arm, cy - half, z1],
            [cx + arm, cy + half, z1],
            [cx + half, cy + half, z1],
            [cx + half, cy + arm, z1],
            [cx - half, cy + arm, z1],
            [cx - half, cy + half, z1],
            [cx - arm, cy + half, z1]
        ])
    }
}
